// File: src/pages/CustomerCost.jsx
// ✅ Updates included (your latest request):
// - If Board Quality is NON PRINTED:
//   Box 7 changes to **Processing Fee Amount**
//   Processing Fee Amount = (Board Charges value in Box 7) * (Box 5 Total Weight)
//   And this Processing Fee Amount is ADDED into Box 9 Total Cost
//
// - Printed logic stays same:
//   Box 7 = Fixed Printing Charges (₹) from Board Defaults (conversionCost)
//   Total Cost = ((LandedCost + ProfitInKg) * TotalWeight) + FixedPrintingCharges

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Container,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Divider,
  Chip,
} from "@mui/material";
import { BaseUrl } from "../BaseUrl";

const PRODUCTS_API = `${BaseUrl}/api/products`;
const BOARD_SETUP_API = `${BaseUrl}/api/board-quality-configs`;

/* simple theme same vibe */
const UI = {
  pageBg: "#EAEDED",
  headerBg: "#131921",
  amazonOrange: "#FF9900",
  amazonOrangeDeep: "#F08804",
  text: "#111",
  sub: "#565959",
  border: "#D5D9D9",
  cardBg: "#fff",
  soft: "#F7F8F8",
};

const norm = (v) => (v ?? "").toString().trim().toLowerCase();
const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
const str = (v) => (v ?? "").toString();

function isPrintedCfg(cfg) {
  if (!cfg) return false;
  const pt = norm(cfg?.printType);
  if (pt === "printed") return true;
  if (cfg?.isPrinted === true) return true;
  return false;
}

export default function CustomerCost() {
  // ✅ dummy customers for now
  const dummyCustomers = useMemo(
    () => [
      { id: "C001", name: "Ajay Traders" },
      { id: "C002", name: "Maa Enterprise" },
      { id: "C003", name: "Jain Packaging" },
      { id: "C004", name: "Rupioo Retail" },
    ],
    [],
  );

  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);

  const [boardLoading, setBoardLoading] = useState(false);
  const [boardCfgs, setBoardCfgs] = useState([]);

  // selections (6 boxes)
  const [customerId, setCustomerId] = useState("");
  const [boardQuality, setBoardQuality] = useState("");
  const [category, setCategory] = useState("");
  const [productName, setProductName] = useState("");
  const [gsm, setGsm] = useState(""); // ✅ GSM dropdown (from board setup group)
  const [numericValue, setNumericValue] = useState(""); // ✅ Order Qty (pcs)

  // ✅ calculation inputs
  const [sheetL, setSheetL] = useState("");
  const [sheetB, setSheetB] = useState("");
  const [purchasePrice, setPurchasePrice] = useState(""); // ₹/kg (Board landedCost)
  const [conversionCost, setConversionCost] = useState(""); // printed: fixed ₹ (conversionCost), non-printed: charges value used for fee rate
  const [processingCost, setProcessingCost] = useState(""); // Board profit in KG (processingCost)

  // fetch products
  useEffect(() => {
    let mounted = true;
    setLoading(true);

    axios
      .get(PRODUCTS_API)
      .then((res) => {
        if (!mounted) return;
        const arr = Array.isArray(res.data) ? res.data : [];
        setProducts(arr);
      })
      .catch((err) => {
        console.error("CustomerCost products fetch error:", err);
        if (!mounted) return;
        setProducts([]);
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  // fetch board configs
  useEffect(() => {
    let mounted = true;
    setBoardLoading(true);

    axios
      .get(BOARD_SETUP_API)
      .then((res) => {
        if (!mounted) return;
        const arr = Array.isArray(res.data) ? res.data : [];
        setBoardCfgs(arr);
      })
      .catch((err) => {
        console.error("CustomerCost board cfg fetch error:", err);
        if (!mounted) return;
        setBoardCfgs([]);
      })
      .finally(() => {
        if (!mounted) return;
        setBoardLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const getBoardCfg = (bq) => {
    const key = norm(bq);
    if (!key) return null;
    return (
      boardCfgs.find((x) => norm(x?.boardQuality) === key) ||
      boardCfgs.find((x) => norm(x?.BoardQuality) === key) ||
      null
    );
  };

  const selectedBoardCfg = useMemo(
    () => getBoardCfg(boardQuality),
    [boardQuality, boardCfgs],
  );

  const printed = useMemo(
    () => isPrintedCfg(selectedBoardCfg),
    [selectedBoardCfg],
  );

  // ✅ boardQuality list comes from Board Setup
  const boardQualityList = useMemo(() => {
    const set = new Set();
    for (const c of boardCfgs) {
      const bq = (c?.boardQuality ?? c?.BoardQuality ?? "").toString().trim();
      if (bq) set.add(bq);
    }
    return [...set].sort((a, b) => norm(a).localeCompare(norm(b)));
  }, [boardCfgs]);

  // category list (depends on boardQuality)
  const categoryList = useMemo(() => {
    const set = new Set();
    for (const p of products) {
      const okBQ = boardQuality ? p?.boardQuality === boardQuality : true;
      if (okBQ && p?.category) set.add(p.category);
    }
    return [...set].sort((a, b) => norm(a).localeCompare(norm(b)));
  }, [products, boardQuality]);

  // product name list (depends on boardQuality + category)
  const productNameList = useMemo(() => {
    const set = new Set();
    for (const p of products) {
      const okBQ = boardQuality ? p?.boardQuality === boardQuality : true;
      const okCat = category ? p?.category === category : true;
      if (okBQ && okCat && p?.name) set.add(p.name);
    }
    return [...set].sort((a, b) => norm(a).localeCompare(norm(b)));
  }, [products, boardQuality, category]);

  // selected customer
  const selectedCustomer = useMemo(() => {
    return dummyCustomers.find((c) => c.id === customerId) || null;
  }, [dummyCustomers, customerId]);

  // ✅ selected product (ignore GSM first)
  const selectedProductBase = useMemo(() => {
    if (!products.length) return null;

    const match = products.find((p) => {
      const okBQ = boardQuality ? p?.boardQuality === boardQuality : true;
      const okCat = category ? p?.category === category : true;
      const okName = productName ? p?.name === productName : true;
      return okBQ && okCat && okName;
    });

    return match || null;
  }, [products, boardQuality, category, productName]);

  // ✅ applicable GSM group list from Board Setup (A/B)
  const gsmGroupInfo = useMemo(() => {
    const cfg = selectedBoardCfg;
    const A = Array.isArray(cfg?.productGsm?.A) ? cfg.productGsm.A : [];
    const B = Array.isArray(cfg?.productGsm?.B) ? cfg.productGsm.B : [];

    const productGsm = selectedProductBase?.orderSheetGsm;
    const pg =
      productGsm === undefined || productGsm === null ? "" : String(productGsm);

    const inA = pg && A.map((x) => String(x)).includes(pg);
    const inB = pg && B.map((x) => String(x)).includes(pg);

    let group = "B";
    if (inB) group = "B";
    else if (inA) group = "A";
    else group = B.length ? "B" : "A";

    const listRaw = group === "B" ? B : A;
    const list = (listRaw || []).map((x) => String(x)).filter((x) => x !== "");

    list.sort((a, b) => (parseFloat(a) || 0) - (parseFloat(b) || 0));
    return { group, list };
  }, [selectedBoardCfg, selectedProductBase]);

  const gsmList = useMemo(() => gsmGroupInfo.list || [], [gsmGroupInfo]);

  // ✅ set GSM default when list changes
  useEffect(() => {
    if (!gsmList.length) {
      setGsm("");
      return;
    }

    const cur = String(gsm || "");
    if (cur && gsmList.includes(cur)) return;

    const pGsm = selectedProductBase?.orderSheetGsm;
    const pg = pGsm !== undefined && pGsm !== null ? String(pGsm) : "";
    if (pg && gsmList.includes(pg)) setGsm(pg);
    else setGsm(gsmList[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gsmList, selectedProductBase]);

  // ✅ final selected product (include GSM if chosen)
  const selectedProduct = useMemo(() => {
    if (!products.length) return null;

    const match = products.find((p) => {
      const okBQ = boardQuality ? p?.boardQuality === boardQuality : true;
      const okCat = category ? p?.category === category : true;
      const okName = productName ? p?.name === productName : true;
      const okGsm = gsm ? String(p?.orderSheetGsm ?? "") === String(gsm) : true;
      return okBQ && okCat && okName && okGsm;
    });

    if (match) return match;
    return selectedProductBase;
  }, [products, boardQuality, category, productName, gsm, selectedProductBase]);

  // ✅ Order qty dropdown: 1000 to 5000 (step 100)
  const orderQtyOptions = useMemo(() => {
    const arr = [];
    for (let q = 1000; q <= 5000; q += 100) arr.push(q);
    return arr;
  }, []);

  // ✅ whenever product selection changes, auto-fill sheetL / sheetB if present
  useEffect(() => {
    if (!selectedProduct) return;

    const l = selectedProduct?.orderSheetL;
    const b = selectedProduct?.orderSheetB;

    if (l !== undefined && l !== null && l !== "") setSheetL(String(l));
    if (b !== undefined && b !== null && b !== "") setSheetB(String(b));
  }, [selectedProduct]);

  // ✅ when board quality changes -> pull defaults from Board Setup
  useEffect(() => {
    const cfg = selectedBoardCfg;
    if (!cfg) {
      setPurchasePrice("");
      setProcessingCost("");
      setConversionCost("");
      return;
    }

    // Box 6 Purchase price = landedCost
    setPurchasePrice(
      cfg?.landedCost !== undefined && cfg?.landedCost !== null
        ? String(cfg.landedCost)
        : "",
    );

    // Box 8 Processing cost (Profit in KG) = processingCost
    setProcessingCost(
      cfg?.processingCost !== undefined && cfg?.processingCost !== null
        ? String(cfg.processingCost)
        : "",
    );

    // Box 7:
    // Printed => fixed printing charges from Defaults (conversionCost)
    // Non Printed => Board Charges (conversionCostPercent) used as RATE for processing fee amount
    if (isPrintedCfg(cfg)) {
      setConversionCost(
        cfg?.conversionCost !== undefined && cfg?.conversionCost !== null
          ? String(cfg.conversionCost)
          : "",
      );
    } else {
      setConversionCost(
        cfg?.conversionCostPercent !== undefined &&
          cfg?.conversionCostPercent !== null
          ? String(cfg.conversionCostPercent)
          : "",
      );
    }
  }, [selectedBoardCfg]);

  // change chain resets
  const onChangeBoardQuality = (val) => {
    setBoardQuality(val);
    setCategory("");
    setProductName("");
    setGsm("");
  };

  const onChangeCategory = (val) => {
    setCategory(val);
    setProductName("");
    setGsm("");
  };

  const onChangeProduct = (val) => {
    setProductName(val);
    setGsm("");
  };

  /* ===================== CALCULATION PART (12 boxes) ===================== */
  const calc = useMemo(() => {
    const L = num(sheetL);
    const B = num(sheetB);
    const G = num(gsm);

    // piecePerSheet fallback:
    const pps1 = num(selectedProduct?.piecePerSheet);
    const pps2 =
      num(selectedProduct?.pcsBreadth) * num(selectedProduct?.pcsLength);
    const piecePerSheet = pps1 || pps2 || 1;

    // ✅ TOTAL WEIGHT formula:
    const wtPerSheet = (L * B * G) / 10000000;

    const orderQtyPcs = num(numericValue);
    const sheetsQty = piecePerSheet ? orderQtyPcs / piecePerSheet : 0;

    const wtPerPiece = piecePerSheet ? wtPerSheet / piecePerSheet : 0;
    const wtPerOrder = wtPerSheet * sheetsQty; // ✅ Box 5 Total Weight

    // Box 6 + Box 8 treated as ₹/kg
    const purchase = num(purchasePrice); // landedCost (₹/kg)
    const profitInKg = num(processingCost); // profit in kg (₹/kg)

    // Printed: fixed printing charges (₹)
    const fixedPrintingCharges = printed ? num(conversionCost) : 0;

    // ✅ Non Printed: Processing Fee Amount = (Box7 RATE) * (TotalWeight)
    const processingFeeRate = printed ? 0 : num(conversionCost); // from charges conversionCostPercent (used as direct multiplier)
    const processingFeeAmount = printed ? 0 : processingFeeRate * wtPerOrder;

    // ✅ Total cost:
    // Printed: ((purchase + profitInKg) * wtPerOrder) + fixedPrintingCharges
    // Non Printed: ((purchase + profitInKg) * wtPerOrder) + processingFeeAmount
    const base = (purchase + profitInKg) * wtPerOrder;

    const totalCost = printed
      ? base + fixedPrintingCharges
      : base + processingFeeAmount;

    const ratePerPiece = orderQtyPcs ? totalCost / orderQtyPcs : 0;

    // GST from Board Setup Product GST Rate
    const gstRate = num(selectedBoardCfg?.productGstRate);
    const totalTax = (totalCost * gstRate) / 100;
    const totalOrderValue = totalCost + totalTax;

    return {
      piecePerSheet,
      printed,
      gstRate,

      wtPerSheet,
      sheetsQty,
      wtPerPiece,
      wtPerOrder,

      purchase,
      profitInKg,
      fixedPrintingCharges,

      processingFeeRate,
      processingFeeAmount,

      totalCost,
      ratePerPiece,
      totalTax,
      totalOrderValue,
    };
  }, [
    sheetL,
    sheetB,
    gsm,
    numericValue,
    purchasePrice,
    processingCost,
    conversionCost,
    selectedProduct,
    selectedBoardCfg,
    printed,
  ]);

  const BoxCard = ({ title, children }) => (
    <Paper
      variant="outlined"
      sx={{
        flex: "1 1 220px",
        minWidth: 220,
        borderRadius: 2,
        borderColor: UI.border,
        p: 1.4,
        background: UI.soft,
      }}
    >
      <Typography sx={{ fontWeight: 1000, color: UI.text, mb: 1 }}>
        {title}
      </Typography>
      {children}
    </Paper>
  );

  // dynamic Box 7 label/value
  const box7Title = printed
    ? "7. Fixed Printing Charges (₹) (Board Defaults)"
    : "7. Processing Fee Amount (Board Charges)";
  const box7Label = printed
    ? "Fixed Printing Charges (₹)"
    : "Processing Fee Amount (₹)";
  const box7Value = printed
    ? num(conversionCost).toFixed(2)
    : calc.processingFeeAmount.toFixed(2);

  return (
    <Box sx={{ minHeight: "100vh", background: UI.pageBg }}>
      {/* header */}
      <Box sx={{ background: UI.headerBg, color: "white" }}>
        <Container maxWidth="xl">
          <Box sx={{ py: 1.6, display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Typography sx={{ fontWeight: 1000, fontSize: 20 }}>
              Customer Cost
            </Typography>
            <Typography
              sx={{ fontWeight: 900, fontSize: 16, color: UI.amazonOrange }}
            >
              Printing Product Cost
            </Typography>

            <Box sx={{ flex: 1 }} />

            {loading || boardLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} sx={{ color: "white" }} />
                <Typography sx={{ fontWeight: 900, fontSize: 13 }}>
                  Loading…
                </Typography>
              </Box>
            ) : (
              <Chip
                label={`Products: ${products.length} | Board: ${boardCfgs.length}`}
                sx={{
                  fontWeight: 1000,
                  bgcolor: "rgba(255,255,255,0.12)",
                  color: "white",
                }}
              />
            )}
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 2.2 }}>
        {/* ===================== SELECTION PART (6 boxes) ===================== */}
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 2,
            background: UI.cardBg,
            borderColor: UI.border,
          }}
        >
          <Typography sx={{ fontWeight: 1000, color: UI.text }}>
            Step 1: Select Customer + Product Inputs
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              flexWrap: "wrap",
              alignItems: "stretch",
            }}
          >
            {/* 1 Customer */}
            <BoxCard title="Box 1: Customer">
              <FormControl fullWidth size="small">
                <InputLabel>Select Customer</InputLabel>
                <Select
                  label="Select Customer"
                  value={customerId}
                  onChange={(e) => setCustomerId(e.target.value)}
                >
                  {dummyCustomers.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </BoxCard>

            {/* 2 BoardQuality */}
            <BoxCard title="Box 2: Paper Quality (from Board Setup)">
              <FormControl fullWidth size="small" disabled={boardLoading}>
                <InputLabel>Board Quality</InputLabel>
                <Select
                  label="Board Quality"
                  value={boardQuality}
                  onChange={(e) => onChangeBoardQuality(e.target.value)}
                >
                  {boardQualityList.map((bq, idx) => (
                    <MenuItem key={idx} value={bq}>
                      {bq}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {!boardLoading && boardQuality && selectedBoardCfg ? (
                <Typography sx={{ mt: 0.8, fontSize: 12.5, color: UI.sub }}>
                  Type: {printed ? "Printed" : "Non Printed"}
                </Typography>
              ) : null}

              {!boardLoading && boardQuality && !selectedBoardCfg ? (
                <Typography sx={{ mt: 0.8, fontSize: 12.5, color: "#B12704" }}>
                  This board quality is not found in Board Setup.
                </Typography>
              ) : null}
            </BoxCard>

            {/* 3 Category */}
            <BoxCard title="Box 3: Category">
              <FormControl
                fullWidth
                size="small"
                disabled={loading || !boardQuality}
              >
                <InputLabel>Category</InputLabel>
                <Select
                  label="Category"
                  value={category}
                  onChange={(e) => onChangeCategory(e.target.value)}
                >
                  {categoryList.map((cat, idx) => (
                    <MenuItem key={idx} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </BoxCard>

            {/* 4 Product */}
            <BoxCard title="Box 4: Product Name">
              <FormControl
                fullWidth
                size="small"
                disabled={loading || !boardQuality || !category}
              >
                <InputLabel>Product</InputLabel>
                <Select
                  label="Product"
                  value={productName}
                  onChange={(e) => onChangeProduct(e.target.value)}
                >
                  {productNameList.map((nm, idx) => (
                    <MenuItem key={idx} value={nm}>
                      {nm}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </BoxCard>

            {/* 5 GSM dropdown (Board setup applicable group) */}
            <BoxCard title="Box 5: GSM (Board Setup Applicable Group)">
              <FormControl
                fullWidth
                size="small"
                disabled={boardLoading || !boardQuality || gsmList.length === 0}
              >
                <InputLabel>GSM (Group {gsmGroupInfo.group || "—"})</InputLabel>
                <Select
                  label={`GSM (Group ${gsmGroupInfo.group || "—"})`}
                  value={gsm}
                  onChange={(e) => setGsm(e.target.value)}
                >
                  {gsmList.map((g, idx) => (
                    <MenuItem key={idx} value={g}>
                      {g}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {!boardLoading && boardQuality && gsmList.length === 0 ? (
                <Typography sx={{ mt: 0.8, fontSize: 12.5, color: UI.sub }}>
                  No GSM groups found in Board Setup for this board quality.
                </Typography>
              ) : null}
            </BoxCard>

            {/* 6 Order Qty dropdown (1000-5000) */}
            <BoxCard title="Box 6: Order Quantity (pcs) (1000–5000)">
              <FormControl fullWidth size="small">
                <InputLabel>Order Qty</InputLabel>
                <Select
                  label="Order Qty"
                  value={numericValue}
                  onChange={(e) => setNumericValue(String(e.target.value))}
                >
                  {orderQtyOptions.map((q) => (
                    <MenuItem key={q} value={q}>
                      {q}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography sx={{ mt: 0.8, fontSize: 12.5, color: UI.sub }}>
                Min 1000, Max 5000
              </Typography>
            </BoxCard>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* preview */}
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 2,
              borderColor: UI.amazonOrangeDeep,
              background: "#FFF3E0",
            }}
          >
            <Typography sx={{ fontWeight: 1000, color: UI.text }}>
              Current Selection
            </Typography>

            <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Chip
                label={`Customer: ${selectedCustomer?.name || "—"}`}
                sx={{ fontWeight: 900 }}
              />
              <Chip
                label={`Board: ${boardQuality || "—"} (${printed ? "Printed" : "Non Printed"})`}
                sx={{ fontWeight: 900 }}
              />
              <Chip
                label={`Category: ${category || "—"}`}
                sx={{ fontWeight: 900 }}
              />
              <Chip
                label={`Product: ${productName || "—"}`}
                sx={{ fontWeight: 900 }}
              />
              <Chip
                label={`GSM (Group ${gsmGroupInfo.group || "—"}): ${gsm || "—"}`}
                sx={{ fontWeight: 900 }}
              />
              <Chip
                label={`Qty(pcs): ${numericValue || "—"}`}
                sx={{ fontWeight: 900 }}
              />
              <Chip
                label={`Piece/Sheet: ${calc.piecePerSheet}`}
                sx={{ fontWeight: 900 }}
              />
              <Chip
                label={`Product GST (Board): ${calc.gstRate}%`}
                sx={{ fontWeight: 900 }}
              />
            </Box>
          </Paper>
        </Paper>

        {/* ===================== CALCULATION PART (12 boxes) ===================== */}
        <Paper
          variant="outlined"
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            background: UI.cardBg,
            borderColor: UI.border,
          }}
        >
          <Typography sx={{ fontWeight: 1000, color: UI.text }}>
            Step 2: Calculation Part
          </Typography>
          <Typography sx={{ color: UI.sub, fontWeight: 700, mt: 0.5 }}>
            (Auto-calculated boxes are read-only.)
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            {/* 1 Sheet L */}
            <BoxCard title="1. Sheet L">
              <TextField
                size="small"
                fullWidth
                label="Sheet L"
                value={sheetL}
                onChange={(e) => {
                  const v = e.target.value;
                  if (/^[0-9]*\.?[0-9]*$/.test(v)) setSheetL(v);
                }}
              />
            </BoxCard>

            {/* 2 Sheet B */}
            <BoxCard title="2. Sheet B">
              <TextField
                size="small"
                fullWidth
                label="Sheet B"
                value={sheetB}
                onChange={(e) => {
                  const v = e.target.value;
                  if (/^[0-9]*\.?[0-9]*$/.test(v)) setSheetB(v);
                }}
              />
            </BoxCard>

            {/* 3 GSM */}
            <BoxCard title="3. GSM">
              <TextField
                size="small"
                fullWidth
                label="GSM"
                value={gsm}
                InputProps={{ readOnly: true }}
              />
              <Typography sx={{ mt: 0.8, fontSize: 12.5, color: UI.sub }}>
                From Board Setup Group dropdown.
              </Typography>
            </BoxCard>

            {/* 4 Wt per piece */}
            <BoxCard title="4. Wt per piece">
              <TextField
                size="small"
                fullWidth
                label="Wt/Piece"
                value={calc.wtPerPiece.toFixed(6)}
                InputProps={{ readOnly: true }}
              />
            </BoxCard>

            {/* 5 Wt per order (TOTAL ORDER WEIGHT) */}
            <BoxCard title="5. Wt per order (Total Weight)">
              <TextField
                size="small"
                fullWidth
                label="Total Weight"
                value={calc.wtPerOrder.toFixed(6)}
                InputProps={{ readOnly: true }}
              />
              <Typography sx={{ mt: 0.8, fontSize: 12.5, color: UI.sub }}>
                TotalWeight = ((SheetL×SheetB×GSM)/10000000) ×
                (Qty/PiecePerSheet)
              </Typography>
            </BoxCard>

            {/* 6 Purchase price - from board setup landedCost */}
            <BoxCard title="6. Purchase price (₹/kg) (Board Landed Cost)">
              <TextField
                size="small"
                fullWidth
                label="Purchase price (₹/kg)"
                value={purchasePrice}
                InputProps={{ readOnly: true }}
              />
            </BoxCard>

            {/* 7 dynamic */}
            <BoxCard title={box7Title}>
              <TextField
                size="small"
                fullWidth
                label={box7Label}
                value={box7Value}
                InputProps={{ readOnly: true }}
              />

              {!printed ? (
                <Typography sx={{ mt: 0.8, fontSize: 12.5, color: UI.sub }}>
                  ProcessingFeeAmount = (Board Charges Rate{" "}
                  {num(conversionCost).toFixed(2)}) × (TotalWeight)
                </Typography>
              ) : (
                <Typography sx={{ mt: 0.8, fontSize: 12.5, color: UI.sub }}>
                  Fixed printing charges added directly to total cost.
                </Typography>
              )}
            </BoxCard>

            {/* 8 Processing cost - from board setup profit in kg */}
            <BoxCard title="8. Processing cost (%) (Board Profit in KG)">
              <TextField
                size="small"
                fullWidth
                label="Profit in KG"
                value={processingCost}
                InputProps={{ readOnly: true }}
              />
              <Typography sx={{ mt: 0.8, fontSize: 12.5, color: UI.sub }}>
                Used as ₹/kg add-on in Total Cost formula.
              </Typography>
            </BoxCard>

            {/* 9 Total cost */}
            <BoxCard title="9. Total cost">
              <TextField
                size="small"
                fullWidth
                label="Total cost"
                value={calc.totalCost.toFixed(2)}
                InputProps={{ readOnly: true }}
              />
              <Typography sx={{ mt: 0.8, fontSize: 12.5, color: UI.sub }}>
                {printed
                  ? "TotalCost = ((LandedCost + ProfitInKg) × TotalWeight) + FixedPrintingCharges"
                  : "TotalCost = ((LandedCost + ProfitInKg) × TotalWeight) + ProcessingFeeAmount"}
              </Typography>
            </BoxCard>

            {/* 10 Rate per piece */}
            <BoxCard title="10. Rate per piece">
              <TextField
                size="small"
                fullWidth
                label="Rate/piece"
                value={calc.ratePerPiece.toFixed(4)}
                InputProps={{ readOnly: true }}
              />
            </BoxCard>

            {/* 11 Total tax */}
            <BoxCard title="11. Total tax (Board Product GST Rate)">
              <TextField
                size="small"
                fullWidth
                label={`Tax (GST ${calc.gstRate}%)`}
                value={calc.totalTax.toFixed(2)}
                InputProps={{ readOnly: true }}
              />
            </BoxCard>

            {/* 12 Total order value */}
            <BoxCard title="12. Total order value">
              <TextField
                size="small"
                fullWidth
                label="Total order value"
                value={calc.totalOrderValue.toFixed(2)}
                InputProps={{ readOnly: true }}
              />
            </BoxCard>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* quick summary line */}
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 2,
              borderColor: UI.amazonOrangeDeep,
              background: "#F0F7FF",
            }}
          >
            <Typography sx={{ fontWeight: 1000, color: UI.text }}>
              Summary
            </Typography>

            <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Chip
                label={`Total Weight: ${calc.wtPerOrder.toFixed(6)} kg`}
                sx={{ fontWeight: 900 }}
              />
              {!printed ? (
                <Chip
                  label={`Processing Fee: ₹${calc.processingFeeAmount.toFixed(2)}`}
                  sx={{ fontWeight: 900 }}
                />
              ) : (
                <Chip
                  label={`Fixed Printing: ₹${calc.fixedPrintingCharges.toFixed(2)}`}
                  sx={{ fontWeight: 900 }}
                />
              )}
              <Chip
                label={`Total Cost: ₹${calc.totalCost.toFixed(2)}`}
                sx={{ fontWeight: 900 }}
              />
              <Chip
                label={`Tax: ₹${calc.totalTax.toFixed(2)}`}
                sx={{ fontWeight: 900 }}
              />
              <Chip
                label={`Order Value: ₹${calc.totalOrderValue.toFixed(2)}`}
                sx={{ fontWeight: 900 }}
              />
            </Box>
          </Paper>
        </Paper>
      </Container>
    </Box>
  );
}
